import { useParams, Link } from 'react-router-dom';
import { games, categories } from '@/mocks/games';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="font-heading text-xl text-foreground-800 mb-2">分类不存在</h2>
          <Link
            to="/"
            className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryGames = games.filter((g) => g.category === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  return (
    <>
      <div className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="mb-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-50 text-primary-500">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          <div className="mb-10">
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {categoryGames.length > 0 && (
            <section className="mb-12">
              <h2 className="font-heading text-xl text-foreground-900 mb-6">
                相关游戏（{categoryGames.length}）
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryGames.map((game) => (
                  <Link
                    key={game.id}
                    to={`/article/${game.slug}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={game.image}
                        alt={game.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50/90 text-xs font-medium text-primary-600 whitespace-nowrap">
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                          {game.rating}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-lg text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">
                        {game.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                        {game.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-foreground-500">
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-user-line"></i>
                          </div>
                          {game.players}
                        </span>
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-time-line"></i>
                          </div>
                          {game.time}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {categoryArticles.length > 0 && (
            <section className="mb-12">
              <h2 className="font-heading text-xl text-foreground-900 mb-6">
                相关攻略（{categoryArticles.length}）
              </h2>
              <div className="flex flex-col gap-4">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col sm:flex-row gap-4 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-full sm:w-48 h-36 rounded-lg overflow-hidden shrink-0">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex flex-col justify-between flex-1 min-w-0">
                      <div>
                        <h3 className="font-heading text-lg text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-foreground-500 mt-3 flex-wrap">
                        <span className="whitespace-nowrap">{article.author}</span>
                        <span className="whitespace-nowrap">{article.date}</span>
                        <span className="whitespace-nowrap">{article.readTime}阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {categoryGames.length === 0 && categoryArticles.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}